
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase, trackEvent, RealtimePayload } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { ArrowDownIcon, ArrowUpIcon, MinusIcon } from "lucide-react";
import { useEffect, useState, useRef } from "react";

interface FinancialSummaryProps {
  period: "month" | "quarter" | "year";
  startDate: string;
  endDate: string;
}

export function FinancialSummary({ period, startDate, endDate }: FinancialSummaryProps) {
  const queryClient = useQueryClient();
  const channelRef = useRef(null);
  const uniqueChannelId = useRef(`summary-${Math.random().toString(36).substring(2, 10)}`);
  
  useEffect(() => {
    console.log(`FinancialSummary: Setting up channel ${uniqueChannelId.current}`);
    
    const channel = supabase
      .channel(uniqueChannelId.current)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'transactions' },
        (payload: RealtimePayload) => {
          console.log('FinancialSummary detected transaction change:', payload.eventType);
          
          // Safely get the event ID
          const eventId = payload.new?.id || payload.old?.id;
          
          // Skip if no event ID or if already processed
          if (!eventId) {
            console.log('FinancialSummary: No event ID found, skipping');
            return;
          }
          
          const isProcessed = trackEvent(`${payload.eventType}-summary`, eventId);
          if (isProcessed) {
            console.log(`FinancialSummary: Event ${payload.eventType}-${eventId} already processed, skipping`);
            return;
          }
          
          console.log(`FinancialSummary: Invalidating queries for ${period}, ${startDate}, ${endDate}`);
          queryClient.invalidateQueries({ 
            queryKey: ["financial-summary", period, startDate, endDate] 
          });
        }
      )
      .subscribe((status) => {
        console.log('FinancialSummary real-time subscription status:', status);
      });
    
    channelRef.current = channel;

    return () => {
      console.log(`FinancialSummary: Cleaning up channel ${uniqueChannelId.current}`);
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [period, startDate, endDate, queryClient]);

  const { data: summary, isLoading } = useQuery({
    queryKey: ["financial-summary", period, startDate, endDate],
    queryFn: async () => {
      let query = supabase
        .from("transactions")
        .select("type, amount");

      if (startDate) {
        query = query.gte("created_at", startDate);
      }
      if (endDate) {
        query = query.lte("created_at", endDate);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Handle case when no data is returned
      if (!data || data.length === 0) {
        return {
          income: 0,
          expenses: 0,
          balance: 0
        };
      }

      const income = data
        .filter((t) => t.type === "entrada")
        .reduce((sum, t) => sum + Number(t.amount), 0);

      const expenses = data
        .filter((t) => t.type === "saida" || t.type === "despesas")
        .reduce((sum, t) => sum + Number(t.amount), 0);

      return {
        income,
        expenses,
        balance: income - expenses,
      };
    },
    staleTime: 10000, // Cache valid for 10 seconds
    retry: 3, // Retry failed queries 3 times
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-pulse">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="p-4 bg-[#222831] border-[#404040]">
            <div className="h-16 bg-gray-700/30 rounded"></div>
          </Card>
        ))}
      </div>
    );
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <Card className="p-4 bg-[#222831] border-[#404040]">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-400">Total de Entradas</p>
            <h4 className="text-2xl font-bold text-green-500">
              {formatCurrency(summary?.income || 0)}
            </h4>
          </div>
          <ArrowUpIcon className="h-8 w-8 text-green-500" />
        </div>
      </Card>

      <Card className="p-4 bg-[#222831] border-[#404040]">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-400">Total de Saídas</p>
            <h4 className="text-2xl font-bold text-red-500">
              {formatCurrency(summary?.expenses || 0)}
            </h4>
          </div>
          <ArrowDownIcon className="h-8 w-8 text-red-500" />
        </div>
      </Card>

      <Card className="p-4 bg-[#222831] border-[#404040]">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-400">Saldo Final</p>
            <h4 
              className={`text-2xl font-bold ${
                (summary?.balance || 0) >= 0 ? "text-green-500" : "text-red-500"
              }`}
            >
              {formatCurrency(summary?.balance || 0)}
            </h4>
          </div>
          <MinusIcon className={`h-8 w-8 ${
            (summary?.balance || 0) >= 0 ? "text-green-500" : "text-red-500"
          }`} />
        </div>
      </Card>
    </div>
  );
}
